import { useEffect, useState } from "react";
import {
    FaLock,
    FaMicrochip,
    FaPlus,
    FaTrash,
    FaUnlink,
    FaSignOutAlt,
    FaSearch,
    FaCircle,
    FaTruck,
    FaBoxOpen,
    FaCheckCircle,
    FaTimesCircle,
    FaArrowUp,
    FaCloudUploadAlt,
    FaSync,
    FaCheckSquare,
    FaRegSquare,
    FaTachometerAlt, FaUsers, FaBell, FaWifi, FaDatabase, FaServer, FaChevronDown
} from "react-icons/fa";

import {
    adminLogin,
    listInventory,
    addInventory,
    deleteInventory,
    forceUnlink,
    setDelivered,
    setActivated,
    listFirmwareReleases,
    registerFirmwareRelease,
    deleteFirmwareRelease,
    pushFirmwareUpdate
} from "../services/admin";

import "../styles/admin.css";
 import CancelUpdateButton from "../components/CancelUpdateButton";
const TOKEN_KEY = "adminToken";

// ---------------------------------------------------------
// Gate: shows the password form until a valid admin token
// exists, then renders the inventory console. This file is
// never linked from the app's nav - it's only reachable by
// typing /admin directly.
// ---------------------------------------------------------
export default function Admin() {

    const [token, setToken] = useState(
        () => localStorage.getItem(TOKEN_KEY) || ""
    );

    if (!token) {
        return <AdminLogin onSuccess={setToken} />;
    }

    return (
        <AdminInventory
            onLogout={() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
            }}
        />
    );

}

// =========================================================
// LOGIN
// =========================================================

function AdminLogin({ onSuccess }) {

    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function handleSubmit(e) {

        e.preventDefault();
        setError("");
        setLoading(true);

        try {

            const res = await adminLogin(password);
            localStorage.setItem(TOKEN_KEY, res.data.token);
            onSuccess(res.data.token);

        } catch (err) {

            setError(
                err.response?.data?.message ||
                "Couldn't log in. Please try again."
            );

        } finally {

            setLoading(false);

        }

    }

    return (

        <div className="adminLoginScreen">

            <form className="adminLoginCard" onSubmit={handleSubmit}>

                <div className="adminLoginIcon"><FaLock /></div>

                <h1>Admin Console</h1>
                <p>Device inventory management</p>

                <input
                    type="password"
                    placeholder="Admin password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                />

                {error && <div className="adminError">{error}</div>}

                <button type="submit" disabled={!password || loading}>
                    {loading ? "Checking..." : "Enter"}
                </button>

            </form>

        </div>

    );

}

// =========================================================
// INVENTORY CONSOLE
// =========================================================

function AdminInventory({ onLogout }) {

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [query, setQuery] = useState("");

    // Pagination - client-side, stays on this same page
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    // Add-device form
    const [serials, setSerials] = useState("");
    const [firmwareVersion, setFirmwareVersion] = useState("");
    const [hardwareVersion, setHardwareVersion] = useState("");
    const [dateProduced, setDateProduced] = useState("");
    const [delivered, setDeliveredField] = useState(false);
    const [notes, setNotes] = useState("");
    const [adding, setAdding] = useState(false);
    const [addResult, setAddResult] = useState(null);
    const [addError, setAddError] = useState("");

    const [busySerial, setBusySerial] = useState(null);

    // Password re-confirmation gate for destructive actions (delete,
    // unlink, deactivate, push firmware). `pendingAction.run(password)`
    // is called once the admin submits the password; it should throw
    // on failure so the modal can show the error and stay open.
    const [pendingAction, setPendingAction] = useState(null);

    function confirmDestructive(message, run) {
        setPendingAction({ message, run });
    }

    // Firmware releases
    const [releases, setReleases] = useState([]);
    const [newVersion, setNewVersion] = useState("");
    const [newFilename, setNewFilename] = useState("");
    const [newNotes, setNewNotes] = useState("");
    const [registering, setRegistering] = useState(false);
    const [registerError, setRegisterError] = useState("");

    // Push-update controls
    const [pushVersion, setPushVersion] = useState("latest");
    const [pushTarget, setPushTarget] = useState("all"); // "all" | "outdated" | "selected"
    const [selectedSerials, setSelectedSerials] = useState([]);
    const [pushing, setPushing] = useState(false);
    const [pushResult, setPushResult] = useState(null);
    const [pushError, setPushError] = useState("");

    useEffect(() => {

        load();
        loadReleases();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep device status (online/offline, OTA progress) current in the
    // background. Uses load({ silent: true }) so the table never gets
    // swapped out for the "Loading..." placeholder mid-poll - that's
    // what used to collapse/expand the page height and yank the
    // admin's scroll position back to the top every few seconds.
    // Runs once on mount and keeps going for the life of the page,
    // independent of `items`, so the interval itself never gets torn
    // down and recreated (which would also just be wasted churn).
    useEffect(() => {

        const timer = setInterval(() => load({ silent: true }), 10000);

        return () => clearInterval(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadReleases() {

        try {

            const res = await listFirmwareReleases();
            setReleases(res.data.releases || []);

        } catch (err) {

            console.error("Failed to load firmware releases:", err);

        }

    }

    // `silent` is used by the background poll below - it refreshes
    // the data without swapping the list out for the "Loading..."
    // placeholder, which used to collapse/re-expand the page height
    // every 8s and yank the admin's scroll position back to the top.
    async function load({ silent = false } = {}) {

        if (!silent) setLoading(true);
        setLoadError(null);

        try {

            const res = await listInventory();
            setItems(res.data.inventory || []);

        } catch (err) {

            if (err.response?.status === 401 || err.response?.status === 403) {

                localStorage.removeItem(TOKEN_KEY);
                onLogout();
                return;

            }

            // Don't clobber a perfectly good list with an error banner
            // just because one background poll hiccuped.
            if (!silent) {
                setLoadError(
                    err.response?.data?.message ||
                    `Couldn't load inventory (error ${err.response?.status || "?"}).`
                );
            }

        } finally {

            if (!silent) setLoading(false);

        }

    }

    async function handleAdd(e) {

        e.preventDefault();

        setAdding(true);
        setAddError("");
        setAddResult(null);

        try {

            const res = await addInventory({
                serials,
                firmware_version: firmwareVersion || null,
                hardware_version: hardwareVersion || null,
                date_produced: dateProduced || null,
                delivered,
                notes: notes || null
            });

            setAddResult(res.data);
            setSerials("");
            setNotes("");
            load();

        } catch (err) {

            setAddError(
                err.response?.data?.message ||
                "Couldn't add device(s). Please try again."
            );

        } finally {

            setAdding(false);

        }

    }

    function handleDelete(serial) {

        confirmDestructive(
            `Remove ${serial} from inventory permanently? This can't be undone.`,
            async (password) => {

                setBusySerial(serial);

                try {

                    await deleteInventory(serial, password);
                    setItems(prev => prev.filter(i => i.serial_no !== serial));

                } finally {

                    setBusySerial(null);

                }

            }
        );

    }

    function handleForceUnlink(serial) {

        confirmDestructive(
            `Unlink ${serial} from its current account? The owner will lose access to it and can't re-add it themselves - they'd need to be told to.`,
            async (password) => {

                setBusySerial(serial);

                try {

                    await forceUnlink(serial, password);
                    load();

                } finally {

                    setBusySerial(null);

                }

            }
        );

    }

    async function handleToggleDelivered(item) {

        setBusySerial(item.serial_no);

        try {

            await setDelivered(item.serial_no, !item.delivered);

            setItems(prev => prev.map(i =>
                i.serial_no === item.serial_no
                    ? { ...i, delivered: !i.delivered }
                    : i
            ));

        } catch (err) {

            alert(err.response?.data?.message || "Couldn't update delivery status.");

        } finally {

            setBusySerial(null);

        }

    }

    // "Activate" is separate from "Delivered" - this is the flag that
    // actually gates whether a customer can register this serial at all.
    function handleToggleActivated(item) {

        const nextActivated = !item.activated;

        async function apply(password) {

            setBusySerial(item.serial_no);

            try {

                await setActivated(item.serial_no, nextActivated, password);

                setItems(prev => prev.map(i =>
                    i.serial_no === item.serial_no
                        ? { ...i, activated: nextActivated }
                        : i
                ));

            } finally {

                setBusySerial(null);

            }

        }

        if (nextActivated) {
            // Activating isn't destructive - no confirmation needed.
            apply(undefined).catch(err => {
                alert(err.response?.data?.message || "Couldn't update activation status.");
            });
            return;
        }

        confirmDestructive(
            `Deactivate ${item.serial_no}? Customers will no longer be able to register this serial.`,
            apply
        );

    }

    async function handleRegisterRelease(e) {

        e.preventDefault();

        setRegistering(true);
        setRegisterError("");

        try {

            await registerFirmwareRelease({
                version: newVersion.trim(),
                filename: newFilename.trim(),
                notes: newNotes.trim() || null
            });

            setNewVersion("");
            setNewFilename("");
            setNewNotes("");
            loadReleases();

        } catch (err) {

            setRegisterError(
                err.response?.data?.message ||
                "Couldn't register that release. Please try again."
            );

        } finally {

            setRegistering(false);

        }

    }

    async function handleDeleteRelease(version) {

        if (!window.confirm(`Remove ${version} from the release list? (The file on disk is untouched.)`))
            return;

        try {

            await deleteFirmwareRelease(version);
            setReleases(prev => prev.filter(r => r.version !== version));

        } catch (err) {

            alert(err.response?.data?.message || "Couldn't remove that release.");

        }

    }

    function toggleSelectSerial(serial) {

        setSelectedSerials(prev =>
            prev.includes(serial)
                ? prev.filter(s => s !== serial)
                : [...prev, serial]
        );

    }

    async function runPush(version, target, password) {

        setPushing(true);
        setPushError("");
        setPushResult(null);

        try {

            const res = await pushFirmwareUpdate({ version, target, confirmPassword: password });

            setPushResult(res.data);
            load({ silent: true }); // reflect the new "queued" status immediately

        } catch (err) {

            setPushError(
                err.response?.data?.message ||
                "Couldn't queue that update. Please try again."
            );

            throw err; // let the confirmation modal know it failed too

        } finally {

            setPushing(false);

        }

    }

    function handlePushUpdate() {

        const target =
            pushTarget === "selected" ? selectedSerials : pushTarget;

        if (pushTarget === "selected" && selectedSerials.length === 0) {

            setPushError("Select at least one device first.");
            return;

        }

        const count = pushTarget === "selected" ? selectedSerials.length : null;

        confirmDestructive(
            count
                ? `Push firmware ${pushVersion} to ${count} selected device${count === 1 ? "" : "s"}?`
                : `Push firmware ${pushVersion} to ${pushTarget === "all" ? "all activated" : "all outdated"} devices?`,
            (password) => runPush(pushVersion, target, password)
        );

    }

    function handleUpdateOne(serial) {

        confirmDestructive(
            `Push the latest firmware to ${serial}?`,
            (password) => runPush("latest", [serial], password)
        );

    }

    const filtered = items.filter(i => {

        const q = query.trim().toLowerCase();
        if (!q) return true;

        return (
            i.serial_no?.toLowerCase().includes(q) ||
            i.firmware_version?.toLowerCase().includes(q) ||
            i.hardware_version?.toLowerCase().includes(q) ||
            i.owner_email?.toLowerCase().includes(q)
        );

    });

    // A search or a page-size change can easily leave `page`
    // pointing past the end of the new result set - clamp it.
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);

    useEffect(() => {

        if (page !== safePage) setPage(safePage);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtered.length, pageSize]);

    const paginated = filtered.slice(
        (safePage - 1) * pageSize,
        safePage * pageSize
    );

    function handleQueryChange(value) {
        setQuery(value);
        setPage(1);
    }

    const stats = {
        total: items.length,
        available: items.filter(i => i.inventory_status === "available").length,
        registered: items.filter(i => i.inventory_status === "registered").length,
        online: items.filter(i => i.live_status === "Online").length,
        delivered: items.filter(i => i.delivered).length,
        activated: items.filter(i => i.activated).length
    };

    const latestVersion = releases[0]?.version || null;

    return (

        <div className="adminShell adminDashboardShell">
            <aside className="adminSidebar">
                <div className="adminBrand"><div className="adminBrandMark"><FaMicrochip /></div><div><strong>TROVA</strong><span>ADMIN</span></div></div>
                <nav className="adminNav">
                    <div className="adminNavLabel">OVERVIEW</div>
                    <button className="adminNavItem active" onClick={() => document.getElementById("admin-overview")?.scrollIntoView({behavior:"smooth"})}><FaTachometerAlt /> Dashboard</button>
                    <div className="adminNavLabel">MANAGEMENT</div>
                    <button className="adminNavItem" onClick={() => document.getElementById("admin-devices")?.scrollIntoView({behavior:"smooth"})}><FaMicrochip /> Devices</button>
                    <button className="adminNavItem" onClick={() => document.getElementById("admin-firmware")?.scrollIntoView({behavior:"smooth"})}><FaCloudUploadAlt /> Firmware</button>
                    <button className="adminNavItem" onClick={() => document.getElementById("admin-devices")?.scrollIntoView({behavior:"smooth"})}><FaUsers /> Users</button>
                    <button className="adminNavItem" onClick={() => document.getElementById("admin-devices")?.scrollIntoView({behavior:"smooth"})}><FaBell /> Alerts</button>
                    <div className="adminNavLabel">SYSTEM</div>
                    <button className="adminNavItem" onClick={() => document.getElementById("admin-overview")?.scrollIntoView({behavior:"smooth"})}><FaWifi /> MQTT</button>
                    <button className="adminNavItem" onClick={() => document.getElementById("admin-overview")?.scrollIntoView({behavior:"smooth"})}><FaDatabase /> Database</button>
                    <button className="adminNavItem" onClick={() => document.getElementById("admin-overview")?.scrollIntoView({behavior:"smooth"})}><FaServer /> Server</button>
                </nav>
                <button className="adminSidebarLogout" onClick={onLogout}><FaSignOutAlt /> Log out</button>
            </aside>
            <main className="adminMain">
                <div className="adminTopbar"><div><span className="adminTopbarKicker">ADMINISTRATION</span><h1>Admin Dashboard</h1></div><div className="adminTopbarRight"><button className="adminIconBtn" title="Notifications"><FaBell /></button><div className="adminUserMenu"><span className="adminAvatar">A</span><span>Admin</span><FaChevronDown /></div></div></div>
                <section id="admin-overview" className="adminOverview">
                    <div className="adminWelcome"><div><h2>Good evening, Admin</h2><p>System overview and device operations</p></div><div className="adminLivePill"><FaCircle /> System Live</div></div>
                    <div className="adminOverviewGrid">
                        <div className="adminMetricCard"><div className="metricIcon"><FaMicrochip /></div><span>DEVICES</span><strong>{stats.total}</strong><small>{stats.activated} activated</small></div>
                        <div className="adminMetricCard metricOnline"><div className="metricIcon"><FaWifi /></div><span>ONLINE</span><strong>{stats.online}</strong><small>Live connections</small></div>
                        <div className="adminMetricCard metricOffline"><div className="metricIcon"><FaTimesCircle /></div><span>OFFLINE</span><strong>{Math.max(0, stats.total - stats.online)}</strong><small>Need attention</small></div>
                        <div className="adminMetricCard"><div className="metricIcon"><FaUsers /></div><span>USERS</span><strong>{stats.registered}</strong><small>{stats.delivered} delivered</small></div>
                    </div>
                    <div className="adminHealthGrid"><div className="adminHealthCard"><div><span>MQTT BROKER</span><strong>Online</strong></div><FaWifi /></div><div className="adminHealthCard"><div><span>DATABASE</span><strong>Connected</strong></div><FaDatabase /></div></div>
                    <div className="adminDashboardPanel"><div className="adminPanelHeader"><div><h3>Device Activity</h3><p>Current fleet connectivity snapshot</p></div><span className="adminPanelBadge">LIVE</span></div><div className="adminActivityChart">{[34,48,42,65,57,72,61,78,68,84,76,91].map((height,idx)=><div className="activityBarWrap" key={idx}><div className="activityBar" style={{height:`${Math.min(100,Math.round(height*(stats.total?Math.max(.35,stats.online/stats.total):.35)))}%`}} /></div>)}</div></div>
                    <div className="adminDashboardPanel adminRecentPanel"><div className="adminPanelHeader"><div><h3>Recent Activity</h3><p>Latest system events</p></div></div><div className="adminActivityList"><div><FaCircle /><span>Device fleet status refreshed</span><time>Live</time></div><div><FaCircle /><span>{stats.online} device{stats.online===1?"":"s"} currently online</span><time>Now</time></div><div><FaCircle /><span>{latestVersion?`Firmware ${latestVersion} is the latest release`:"No firmware release registered yet"}</span><time>System</time></div></div></div>
                </section>
                <header className="adminHeader">

                <div>
                    <h1><FaMicrochip /> Device Inventory</h1>
                    <p>Internal console - not linked anywhere in the app</p>
                </div>

                <button className="adminLogoutBtn" onClick={onLogout}>
                    <FaSignOutAlt /> Log out
                </button>

            </header>

            <div className="adminStatsRow">
                <div className="adminStatChip"><span>Total</span><b>{stats.total}</b></div>
                <div className="adminStatChip"><span>Available</span><b>{stats.available}</b></div>
                <div className="adminStatChip"><span>Registered</span><b>{stats.registered}</b></div>
                <div className="adminStatChip online"><span>Online now</span><b>{stats.online}</b></div>
                <div className="adminStatChip"><span>Delivered</span><b>{stats.delivered}</b></div>
                <div className="adminStatChip online"><span>Activated</span><b>{stats.activated}</b></div>
            </div>

            {/* ---------- Add to inventory ---------- */}
            <section id="admin-devices"><form className="adminAddCard" onSubmit={handleAdd}>

                <h2><FaPlus /> Add to Inventory</h2>
                <p className="adminHint">
                    One Device ID, or paste a whole batch (one per line or comma-separated).
                    The fields below apply to all of them.
                </p>

                <textarea
                    placeholder={""}
                    value={serials}
                    onChange={(e) => setSerials(e.target.value)}
                    rows={3}
                />

                <div className="adminAddRow">

                    <input
                        type="text"
                        placeholder="Firmware version (optional)"
                        value={firmwareVersion}
                        onChange={(e) => setFirmwareVersion(e.target.value)}
                    />

                    <input
                        type="text"
                        placeholder="Hardware version (optional)"
                        value={hardwareVersion}
                        onChange={(e) => setHardwareVersion(e.target.value)}
                    />

                </div>

                <div className="adminAddRow">

                    <div className="adminDateField">
                        <label>Date produced</label>
                        <input
                            type="date"
                            value={dateProduced}
                            onChange={(e) => setDateProduced(e.target.value)}
                        />
                    </div>

                    <div className="adminDeliveredField">
                        <label>Delivered</label>
                        <div className="adminToggleGroup">
                            <button
                                type="button"
                                className={!delivered ? "active" : ""}
                                onClick={() => setDeliveredField(false)}
                            >
                                No
                            </button>
                            <button
                                type="button"
                                className={delivered ? "active" : ""}
                                onClick={() => setDeliveredField(true)}
                            >
                                Yes
                            </button>
                        </div>
                    </div>

                </div>

                <input
                    type="text"
                    placeholder="Notes (optional) - batch #, etc."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />

                {addError && <div className="adminError">{addError}</div>}

                {addResult && (
                    <div className="adminSuccess">
                        {addResult.message}
                        {addResult.skipped?.length > 0 && (
                            <div className="adminSkipped">
                                Already existed: {addResult.skipped.join(", ")}
                            </div>
                        )}
                    </div>
                )}

                <button type="submit" disabled={!serials.trim() || adding}>
                    {adding ? "Adding..." : "Add to Inventory"}
                </button>

            </form>

            {/* ---------- Firmware releases ---------- */}
            <div id="admin-firmware"><form className="adminAddCard" onSubmit={handleRegisterRelease}>

                <h2><FaCloudUploadAlt /> Firmware Releases</h2>
                <p className="adminHint">
                    Upload the .bin to this server's firmware folder yourself first
                    (scp/rsync), then register its version + filename here so it
                    becomes selectable below. This does not accept file uploads.
                </p>

                <div className="adminAddRow">

                    <input
                        type="text"
                        placeholder="Version (e.g. 1.2.0)"
                        value={newVersion}
                        onChange={(e) => setNewVersion(e.target.value)}
                        required
                    />

                    <input
                        type="text"
                        placeholder="Filename (e.g. dongle_v1.2.0.bin)"
                        value={newFilename}
                        onChange={(e) => setNewFilename(e.target.value)}
                        required
                    />

                </div>

                <input
                    type="text"
                    placeholder="Notes (optional) - what changed in this release"
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                />

                {registerError && <div className="adminError">{registerError}</div>}

                <button type="submit" disabled={!newVersion.trim() || !newFilename.trim() || registering}>
                    {registering ? "Registering..." : "Register Release"}
                </button>

                {releases.length > 0 && (

                    <div className="releaseList">

                        {releases.map((r, idx) => (

                            <div className="releaseRow" key={r.version}>

                                <div className="releaseInfo">
                                    <b>{r.version}</b>
                                    {idx === 0 && <span className="latestTag">Latest</span>}
                                    <span className="releaseFilename">{r.filename}</span>
                                    {r.notes && <span className="releaseNotes">{r.notes}</span>}
                                </div>

                                <button
                                    type="button"
                                    className="deleteBtn"
                                    onClick={() => handleDeleteRelease(r.version)}
                                    title="Remove from release list"
                                >
                                    <FaTrash />
                                </button>

                            </div>

                        ))}

                    </div>

                )}

            </form></div>

            {/* ---------- Push update ---------- */}
            <div className="adminAddCard">

                <h2><FaSync /> Push Firmware Update</h2>
                <p className="adminHint">
                    Queues an OTA command over MQTT to each matching activated
                    device. Devices report progress back, which shows up in the
                    list below (Queued → Started → Success/Failed).
                </p>

                <div className="adminAddRow">

                    <select
                        value={pushVersion}
                        onChange={(e) => setPushVersion(e.target.value)}
                    >
                        <option value="latest">
                            Latest{latestVersion ? ` (${latestVersion})` : ""}
                        </option>
                        {releases.map(r => (
                            <option key={r.version} value={r.version}>{r.version}</option>
                        ))}
                    </select>

                    <select
                        value={pushTarget}
                        onChange={(e) => setPushTarget(e.target.value)}
                    >
                        <option value="all">All activated devices</option>
                        <option value="outdated">Only outdated devices</option>
                        <option value="selected">Selected devices (check below)</option>
                    </select>

                </div>

                {pushTarget === "selected" && (
                    <p className="adminHint">
                        {selectedSerials.length} device{selectedSerials.length === 1 ? "" : "s"} selected
                        via the checkboxes in the list below.
                    </p>
                )}

                {pushError && <div className="adminError">{pushError}</div>}

                {pushResult && (
                    <div className="adminSuccess">
                        {pushResult.message}
                    </div>
                )}

                <button
                    type="button"
                    onClick={handlePushUpdate}
                    disabled={pushing || releases.length === 0}
                >
                    {pushing ? "Queuing..." : "Push Update"}
                </button>
          
            </div>

            {/* ---------- List ---------- */}
            <div className="adminListCard">

                <div className="adminSearchBar">
                    <FaSearch />
                    <input
                        type="text"
                        placeholder="Search by serial, firmware, hardware, or owner email..."
                        value={query}
                        onChange={(e) => handleQueryChange(e.target.value)}
                    />
                </div>

                {!loading && !loadError && filtered.length > 0 && (

                    <div className="adminPageSizeRow">

                        <span>Showing {paginated.length} of {filtered.length}</span>

                        <label>
                            Per page
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </label>

                    </div>

                )}

                {loading ? (

                    <div className="adminEmpty">Loading inventory...</div>

                ) : loadError ? (

                    <div className="adminEmpty adminErrorState">
                        <p>{loadError}</p>
                        <button onClick={load}>Retry</button>
                    </div>

                ) : filtered.length === 0 ? (

                    <div className="adminEmpty">
                        {items.length === 0
                            ? "No devices in inventory yet."
                            : "No devices match your search."}
                    </div>

                ) : (

                    <div className="adminTable">

                        {paginated.map(i => (

                            <div className="adminRow" key={i.serial_no}>

                                {pushTarget === "selected" && (

                                    <button
                                        type="button"
                                        className="rowSelectBtn"
                                        onClick={() => toggleSelectSerial(i.serial_no)}
                                        title={
                                            selectedSerials.includes(i.serial_no)
                                                ? "Deselect"
                                                : "Select for update"
                                        }
                                    >
                                        {selectedSerials.includes(i.serial_no)
                                            ? <FaCheckSquare />
                                            : <FaRegSquare />
                                        }
                                    </button>

                                )}

                                <div className="adminRowMain">

                                    <div className="adminRowSerial">
                                        {i.serial_no}
                                        <span
                                            className={
                                                i.live_status === "Online"
                                                    ? "liveBadge online"
                                                    : i.live_status === "Offline"
                                                    ? "liveBadge offline"
                                                    : "liveBadge unseen"
                                            }
                                        >
                                            <FaCircle />
                                            {i.live_status === "Online"
                                                ? "Online"
                                                : i.live_status === "Offline"
                                                ? "Offline"
                                                : "Never connected"}
                                        </span>
                                    </div>

                                    <div className="adminRowMeta">
                                        <span>FW {i.firmware_version || "unknown"}</span>
                                        {i.hardware_version && <span>HW {i.hardware_version}</span>}
                                        {i.date_produced && (
                                            <span>
                                                Produced {new Date(i.date_produced).toLocaleDateString()}
                                            </span>
                                        )}
                                        {i.notes && <span className="adminNotes">{i.notes}</span>}
                                    </div>

                                    {i.ota_status && (

                                        <div className={`otaStatusRow ota-${i.ota_status}`}>

                                            {i.ota_status === "success" && <FaCheckCircle />}
                                            {i.ota_status === "failed" && <FaTimesCircle />}
                                            {(i.ota_status === "queued" || i.ota_status === "started") && <FaSync className="spin" />}

                                            {i.ota_status === "queued" && "Update queued"}
                                            {i.ota_status === "started" && `Updating to ${i.ota_target_version}...`}
                                            {i.ota_status === "success" && `Updated to ${i.ota_target_version}`}
                                            {i.ota_status === "no_change" && "Already on this version"}
                                            {i.ota_status === "failed" && (
                                                `Update to ${i.ota_target_version} failed${i.ota_error ? `: ${i.ota_error}` : ""}`
                                            )}

                                        </div>

                                    )}

                                </div>

                                <div className="adminRowOwner">

                                    {i.owner_email ? (
                                        <>
                                            <span className="ownerBadge registered">Registered</span>
                                            <span className="ownerEmail">{i.owner_email}</span>
                                            {i.owner_phone && (
                                                <span className="ownerPhone">{i.owner_phone}</span>
                                            )}
                                            {(i.device_brand || i.device_model) && (
                                                <span className="deviceModel">
                                                    {[i.device_brand, i.device_model].filter(Boolean).join(" ")}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="ownerBadge available">Available</span>
                                    )}

                                </div>

                                <div className="adminRowActions">

                                    <button
                                        className={
                                            i.activated
                                                ? "activatedBtn activated"
                                                : "activatedBtn"
                                        }
                                        disabled={busySerial === i.serial_no}
                                        onClick={() => handleToggleActivated(i)}
                                        title={
                                            i.activated
                                                ? "Deactivate (customers can no longer register this serial)"
                                                : "Activate (allow this serial to be registered)"
                                        }
                                    >
                                        {i.activated ? <FaCheckCircle /> : <FaTimesCircle />}
                                    </button>

                                    <button
                                        className={
                                            i.delivered
                                                ? "deliveredBtn delivered"
                                                : "deliveredBtn"
                                        }
                                        disabled={busySerial === i.serial_no}
                                        onClick={() => handleToggleDelivered(i)}
                                        title={
                                            i.delivered
                                                ? "Mark as not delivered"
                                                : "Mark as delivered"
                                        }
                                    >
                                        {i.delivered ? <FaTruck /> : <FaBoxOpen />}
                                    </button>

                                    <button
                                        className="updateBtn"
                                        disabled={
                                            !i.activated ||
                                            pushing ||
                                            i.ota_status === "queued" ||
                                            i.ota_status === "started" ||
                                            releases.length === 0 ||
                                            i.firmware_version === latestVersion
                                        }
                                       onClick={() => handleUpdateOne(i.serial_no)}
title={
    !i.activated
        ? "Activate this device first"
        : "Push the latest firmware to this device"
}
>
    <FaArrowUp />
</button>

{(i.ota_status === "queued" || i.ota_status === "started") && (
    <CancelUpdateButton serial={i.serial_no} onDone={load} />
)}
                                    {i.owner_email && (
                                        <button
                                            className="unlinkBtn"
                                            disabled={busySerial === i.serial_no}
                                            onClick={() => handleForceUnlink(i.serial_no)}
                                            title="Unlink from account"
                                        >
                                            <FaUnlink />
                                        </button>
                                    )}

                                    <button
                                        className="deleteBtn"
                                        disabled={busySerial === i.serial_no || !!i.owner_email}
                                        onClick={() => handleDelete(i.serial_no)}
                                        title={
                                            i.owner_email
                                                ? "Unlink before deleting"
                                                : "Remove from inventory"
                                        }
                                    >
                                        <FaTrash />
                                    </button>

                                </div>

                            </div>

                        ))}

                    </div>

                )}

                {!loading && !loadError && totalPages > 1 && (

                    <div className="adminPagination">

                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={safePage === 1}
                        >
                            Prev
                        </button>

                        <div className="adminPageNumbers">

                            {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                                .filter(n =>
                                    n === 1 ||
                                    n === totalPages ||
                                    Math.abs(n - safePage) <= 1
                                )
                                .map((n, idx, arr) => (
                                    <span key={n} className="adminPageNumGroup">
                                        {idx > 0 && n - arr[idx - 1] > 1 && (
                                            <span className="adminPageEllipsis">...</span>
                                        )}
                                        <button
                                            className={n === safePage ? "active" : ""}
                                            onClick={() => setPage(n)}
                                        >
                                            {n}
                                        </button>
                                    </span>
                                ))}

                        </div>

                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={safePage === totalPages}
                        >
                            Next
                        </button>

                    </div>

                )}

            </div>
            </section>

            {pendingAction && (
                <PasswordConfirmModal
                    message={pendingAction.message}
                    onCancel={() => setPendingAction(null)}
                    onConfirm={async (password) => {
                        await pendingAction.run(password);
                        setPendingAction(null);
                    }}
                />
            )}

            </main>
        </div>

    );

}

// =========================================================
// PASSWORD CONFIRMATION MODAL
// Re-asks for the admin password before anything destructive
// runs (delete, unlink, deactivate, push firmware) - guards
// against an unattended session being used to do damage.
// =========================================================

function PasswordConfirmModal({ message, onConfirm, onCancel }) {

    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e) {

        e.preventDefault();
        setError("");
        setSubmitting(true);

        try {

            await onConfirm(password);

        } catch (err) {

            setError(
                err.response?.data?.message ||
                "Incorrect password."
            );

        } finally {

            setSubmitting(false);

        }

    }

    return (

        <div className="adminModalOverlay">

            <form className="adminModalCard" onSubmit={handleSubmit}>

                <div className="adminLoginIcon"><FaLock /></div>

                <h2>Confirm admin password</h2>
                <p>{message}</p>

                <input
                    type="password"
                    placeholder="Admin password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                />

                {error && <div className="adminError">{error}</div>}

                <div className="adminModalActions">

                    <button
                        type="button"
                        className="adminModalCancel"
                        onClick={onCancel}
                        disabled={submitting}
                    >
                        Cancel
                    </button>

                    <button type="submit" disabled={!password || submitting}>
                        {submitting ? "Checking..." : "Confirm"}
                    </button>

                </div>

            </form>

        </div>

    );

}
